/**
 * FAQs Search Section Model
 * Valtio-based state for search filtering and "show more" per group
 */
import {proxy} from "valtio";

import type {IValtioModel} from "@/shared/models/base-model.interface";
import {Field, type FieldInstance} from "@/shared/models/form/fields";

import type {ContentApiFaqGroup} from "./type";

const DEFAULT_VISIBLE_COUNT = 3;

export interface FaqsSearchModelInstance extends IValtioModel {
    searchField: FieldInstance;
    expandedGroups: Record<string, boolean>;
    openItems: Record<string, boolean>;

    readonly groups: Array<ContentApiFaqGroup>;

    getFilteredFaqs(group: ContentApiFaqGroup): Array<{id: string; question: string; answer: string}>;
    getVisibleFaqs(group: ContentApiFaqGroup): Array<{id: string; question: string; answer: string}>;
    hasMore(group: ContentApiFaqGroup): boolean;
    toggleGroup(groupId: string): void;
    toggleItem(itemId: string): void;
    isItemOpen(itemId: string): boolean;
    isGroupExpanded(groupId: string): boolean;
}

export function FaqsSearchModel(groups: Array<ContentApiFaqGroup>): FaqsSearchModelInstance {
    return proxy<FaqsSearchModelInstance>({
        searchField: Field({initialValue: ""}),
        expandedGroups: {},
        openItems: {},
        groups,

        getFilteredFaqs(group: ContentApiFaqGroup) {
            const query = this.searchField.value.toLowerCase().trim();
            const faqs = (group.faqs || []).filter((faq) => faq.question && faq.answer);

            if (!query) {
                return faqs.map((faq) => ({
                    id: faq.id,
                    question: faq.question || "",
                    answer: faq.answer || "",
                }));
            }

            return faqs
                .filter((faq) => {
                    const q = (faq.question || "").toLowerCase();
                    const a = (faq.answer || "").toLowerCase();
                    return q.includes(query) || a.includes(query);
                })
                .map((faq) => ({
                    id: faq.id,
                    question: faq.question || "",
                    answer: faq.answer || "",
                }));
        },

        getVisibleFaqs(group: ContentApiFaqGroup) {
            const filtered = this.getFilteredFaqs(group);
            const isExpanded = this.expandedGroups[group.id] === true;
            const isSearching = this.searchField.value.trim().length > 0;

            if (isExpanded || isSearching) {
                return filtered;
            }

            return filtered.slice(0, DEFAULT_VISIBLE_COUNT);
        },

        hasMore(group: ContentApiFaqGroup) {
            const isSearching = this.searchField.value.trim().length > 0;
            if (isSearching) return false;

            const filtered = this.getFilteredFaqs(group);
            const isExpanded = this.expandedGroups[group.id] === true;
            return !isExpanded && filtered.length > DEFAULT_VISIBLE_COUNT;
        },

        toggleGroup(groupId: string) {
            this.expandedGroups[groupId] = !this.expandedGroups[groupId];
        },

        toggleItem(itemId: string) {
            this.openItems[itemId] = !this.openItems[itemId];
        },

        isItemOpen(itemId: string) {
            return this.openItems[itemId] === true;
        },

        isGroupExpanded(groupId: string) {
            return this.expandedGroups[groupId];
        },
    });
}
